import Button from '@ben/orion/deprecated-button';
import InlineTextButton from '@ben/orion/inline-text-button/inline-text-button';
import Input, { InputVariant } from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type {
  ConditionalApprovalConfig,
  IModularBenefitDetail,
} from '@ben/shared/types/columbia';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import ApprovalFlowDropdown from './approval-flow-dropdown';

const ConditionalApprovals = () => {
  const { control } = useFormContext<IModularBenefitDetail>();
  const { fields, append, remove } = useFieldArray({
    name: 'conditionalApprovalConfigs',
    control,
  });

  const employerId = useWatch({ name: 'employer', exact: true });

  const initialConditionalApproval: ConditionalApprovalConfig = {
    name: '',
    approvalFlow: undefined,
    triggerFormula: '',
  };

  const handleAddConditionalApproval = () => {
    append(initialConditionalApproval);
  };

  return (
    <Container>
      <TitleContainer>
        <Typography variant={TypographyVariant.h2}>
          Conditional approvals
        </Typography>
        <Button
          label="Add conditional approval"
          onClick={handleAddConditionalApproval}
        />
      </TitleContainer>
      {fields?.length === 0 ? (
        <Typography variant={TypographyVariant.bodyLg1}>
          No conditional approvals added yet.
        </Typography>
      ) : (
        fields.map((conditionalApproval, index) => (
          <ContentWrapper key={conditionalApproval.id}>
            <TitleContainer>
              <Typography variant={TypographyVariant.h3}>
                Approval #{index + 1}
              </Typography>
              <InlineTextButton label="Remove" onClick={() => remove(index)} />
            </TitleContainer>
            <Row>
              <Controller
                name={`conditionalApprovalConfigs.${index}.name`}
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    name="Name of approval"
                    data-testid={`conditional-approval-name-${index}`}
                    hasError={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                    width="100%"
                  />
                )}
                rules={{
                  required: fieldCannotBeBlankErrorMessage,
                }}
              />
              <ApprovalFlowDropdown
                componentControllerName={`conditionalApprovalConfigs.${index}`}
                employerId={Number(employerId)}
              />
            </Row>
            <Row>
              <Controller
                name={`conditionalApprovalConfigs.${index}.triggerFormula`}
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    name="Trigger formula"
                    data-testid={`conditional-approval-trigger-formula-${index}`}
                    hasError={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                    width="100%"
                    variant={InputVariant.TextArea}
                  />
                )}
                rules={{
                  required: fieldCannotBeBlankErrorMessage,
                }}
              />
            </Row>
          </ContentWrapper>
        ))
      )}
    </Container>
  );
};

export default ConditionalApprovals;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    width: 100%;
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: 10px;
    padding: ${theme.spacing(3)};
  `}
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ContentWrapper = styled(Container)`
  ${({ theme }) => css`
    background-color: ${theme.palette.primary.bg};
    border: none;
  `}
`;

const Row = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${theme.spacing(2)};
  `}
`;
